import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Calendar, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import CustomButton from "../../../../custom/custom-button/CustomButton";
import CustomDatePicker from "../../../../shared/date-picker/Date/CustomDatePicker";
import { Order } from "../../../../../services";
import {
  buildRouteDatesPayload,
  createRouteDateRows,
  getPreviousFilledDate,
  validateRouteDates,
} from "./routeDates";
import style from "./EditRouteDatesModal.module.scss";

const TYPE_TRANSLATIONS = {
  Loading: {
    operation: "orderInfoClient.editRouteDatesModal.loading",
    dateLabel: "orderInfoClient.editRouteDatesModal.loadingDate",
  },
  Unloading: {
    operation: "orderInfoClient.editRouteDatesModal.unloading",
    dateLabel: "orderInfoClient.editRouteDatesModal.unloadingDate",
  },
};

const EditRouteDatesModal = ({ isOpen, order, onCancel, onSuccess }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setRows(createRouteDateRows(order?.addressList));
    setErrors({});
    setIsLoading(false);
  }, [isOpen, order]);

  if (!isOpen) return null;

  const changeDate = (id, date) => {
    setRows((currentRows) =>
      currentRows.map((row) => {
        if (row.id !== id || (!date && row.date)) return row;

        return { ...row, date };
      }),
    );
    setErrors((currentErrors) => {
      if (!currentErrors[id]) return currentErrors;

      const nextErrors = { ...currentErrors };
      delete nextErrors[id];
      return nextErrors;
    });
  };

  const handleConfirm = async () => {
    const nextErrors = validateRouteDates(rows);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) return;

    const { orderId, addressList } = buildRouteDatesPayload(order?.id, rows);

    try {
      setIsLoading(true);
      await Order.updateShippingDate(orderId, addressList);
      toast.success(t("orderInfoClient.editRouteDatesModal.updateSuccess"));
      onSuccess?.();
      onCancel?.();
    } catch (error) {
      console.error("Error updating order route dates:", error);
      toast.error(t("orderInfoClient.editRouteDatesModal.updateError"));
    } finally {
      setIsLoading(false);
    }
  };

  const getPointTitle = (row, index) => {
    if (row.isIntermediate) {
      return t("orderInfoClient.editRouteDatesModal.intermediatePoint", { number: index });
    }

    return index === 0
      ? t("orderInfoClient.editRouteDatesModal.loadingPoint")
      : t("orderInfoClient.editRouteDatesModal.unloadingPoint");
  };

  return ReactDOM.createPortal(
    <div
      className={style.overlay}
      data-portal-modal="true"
      onMouseDown={(event) => {
        if (!isLoading && event.target === event.currentTarget) onCancel?.();
      }}
    >
      <div
        className={style.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-route-dates-title"
      >
        <button
          type="button"
          className={style.closeButton}
          onClick={onCancel}
          disabled={isLoading}
          aria-label={t("orderInfoClient.editRouteDatesModal.close")}
        >
          <X size={26} strokeWidth={1.6} />
        </button>

        <header className={style.header}>
          <h2 id="edit-route-dates-title" className={style.title}>
            {t("orderInfoClient.editRouteDatesModal.title")}
          </h2>
          <p className={style.description}>
            {t("orderInfoClient.editRouteDatesModal.description")}
          </p>
        </header>

        <div className={style.routeList}>
          {rows.map((row, index) => {
            const typeTranslation = TYPE_TRANSLATIONS[row.type] || TYPE_TRANSLATIONS.Unloading;
            const minDate = getPreviousFilledDate(rows, index);

            return (
              <div className={style.routeRow} key={row.id}>
                <div className={style.pointInfo}>
                  <span className={style.routeRail} aria-hidden="true" />
                  <div>
                    <h3 className={style.pointTitle}>{getPointTitle(row, index)}</h3>
                    <div className={style.pointMeta}>
                      {row.isIntermediate && (
                        <span className={style.typeBadge}>{t(typeTranslation.operation)}</span>
                      )}
                      <span className={style.city}>{row.cityName || row.address}</span>
                    </div>
                  </div>
                </div>

                <div className={style.dateField}>
                  <label className={style.dateLabel}>{t(typeTranslation.dateLabel)}</label>
                  <div className={style.inputWithIcon}>
                    <div className={style.datePickerWrapper}>
                      <CustomDatePicker
                        selected={row.date}
                        onChange={(date) => changeDate(row.id, date)}
                        onKeyDown={(event) => {
                          if (row.date && ["Backspace", "Delete"].includes(event.key)) {
                            event.preventDefault();
                          }
                        }}
                        minDate={minDate || undefined}
                        dateFormat="dd.MM.yyyy"
                        placeholderText="ДД.ММ.ГГГГ"
                        popperPlacement="bottom-start"
                        disabled={isLoading}
                      />
                    </div>
                    <Calendar className={style.inputIcon} size={20} />
                  </div>
                  {errors[row.id] && (
                    <span className={style.errorText} role="alert">
                      {t(`orderInfoClient.editRouteDatesModal.validation.${errors[row.id]}`)}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className={style.actions}>
          <CustomButton
            isWhite
            isWhiteBox
            text={t("orderInfoClient.editRouteDatesModal.cancel")}
            onClick={onCancel}
            disabled={isLoading}
          />
          <CustomButton
            isNavy
            text={t("orderInfoClient.editRouteDatesModal.confirm")}
            onClick={handleConfirm}
            isLoading={isLoading}
            disabled={isLoading}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default EditRouteDatesModal;
